import React, {ReactNode} from 'react';
import {Card, Header, Icon} from 'semantic-ui-react';
import TimeAgo from 'react-timeago';
import {History} from 'history';
import Breadcrumbs from '../components/Breadcrumbs';

import {TimeDelta} from '../util/time';
import Markdown from '../components/Markdown';
import Loader from '../components/WandbLoader';
import {Project} from '../graphql/allProjectsQuery';
import {Node} from '../types/graphql';

import '../css/ProjectList.less';
import {useAllProjectsQuery, useModelsQuery} from '../generated/graphql';
import {ProjectActionMenu} from '../components/ProjectActionMenu';
import {Link} from 'react-router-dom';
import makeComp from '../util/profiler';

interface ProjectListProps {
  entityName?: string;
  order?: string;
  limit?: number;
  anonymous?: boolean;
  sortSettings?: ReactNode;
  allowCreate: boolean;
  isFeaturedProjects?: boolean;
  emptyContent: ReactNode;
  history: History;
}

interface ProjectListPropsWithEntityName extends ProjectListProps {
  entityName: string;
}

interface ProjectListInnerProps extends ProjectListProps {
  loading: boolean;
  projects: Array<Node<Project>>;
  enableSortSettings?: boolean;
}

const ProjectListInner: React.FC<ProjectListInnerProps> = makeComp(
  ({
    loading,
    entityName,
    enableSortSettings,
    sortSettings,
    allowCreate,
    projects,
    isFeaturedProjects,
    emptyContent,
    history,
  }) => {
    if (loading) {
      return (
        <div style={{position: 'relative', top: 300}}>
          <Loader />
        </div>
      );
    }
    return (
      <div>
        {enableSortSettings && sortSettings}
        <Card.Group className="project-list link">
          {allowCreate && entityName && (
            <Link
              className="ui card"
              to={`/new-project?entityName=${entityName}`}
              key="__createNew"
              style={{
                backgroundColor: 'rgb(0, 127, 175, 0.05)',
              }}>
              <Card.Content>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 140,
                  }}>
                  <div style={{textAlign: 'center'}}>
                    <Header>Create Project</Header>
                    <Icon name="plus" />
                  </div>
                </div>
              </Card.Content>
            </Link>
          )}
          {projects.map(edge => {
            const project = edge.node;
            let projectPath = `/${project.entityName}/${project.name}/`;
            // if we're displaying featured projects, we want to link to the demo report rather than the project page
            // TODO: ideally, manage featured projects via admin page rather than command line and hardcoded param  =P
            if (isFeaturedProjects) {
              projectPath = projectPath + 'reports/?view=altay%2FDemo%20Report';
            }

            const activity = project.lastActive ? (
              <i>
                Last Active <TimeAgo date={project.lastActive + 'Z'} />
              </i>
            ) : (
              <i>No Activity</i>
            );

            return (
              <Link className="ui card" to={projectPath} key={project.id}>
                <Card.Content style={{flexGrow: project.description ? 0 : 1}}>
                  <Card.Header style={{display: 'flex', alignItems: 'center'}}>
                    <Breadcrumbs
                      style={{marginRight: 12, marginBottom: 5, flexGrow: 1}}
                      entityName={project.entityName}
                      projectName={project.name}
                      disableLinks
                    />
                    <ProjectActionMenu project={project} />
                  </Card.Header>
                  <Card.Meta>{activity}</Card.Meta>
                </Card.Content>
                {project.description && (
                  <Card.Content style={{maxHeight: 160, overflow: 'auto'}}>
                    <Card.Description>
                      <Markdown content={project.description} />
                    </Card.Description>
                  </Card.Content>
                )}
                <Card.Content extra>
                  <p>
                    <Icon name={project.totalUsers === 1 ? 'user' : 'users'} />
                    {project.totalUsers}{' '}
                    {project.totalUsers === 1 ? 'user' : 'users'}
                  </p>
                  <p>
                    <Icon name="rocket" />
                    {project.totalRuns}{' '}
                    {project.totalRuns === 1 ? 'run' : 'runs'}
                  </p>
                  <p>
                    <Icon name="microchip" />{' '}
                    {new TimeDelta(project.totalRunTime).toSingleUnitString()}{' '}
                    of compute time
                  </p>
                </Card.Content>
              </Link>
            );
          })}
        </Card.Group>
        <div style={{marginTop: 20}}>
          {projects.length === 0 && emptyContent}
        </div>
      </div>
    );
  },
  {id: 'ProjectListInner'}
);

const useProjectListProps = ({
  entityName,
  order,
  limit,
  anonymous,
}: ProjectListProps) => {
  const {loading, error, data} = useModelsQuery({
    variables: {entityName, order, first: limit, anonymous},
    fetchPolicy: 'cache-and-network',
  });
  return {
    loading,
    projects: (data?.projects ? data.projects.edges : []) as any[],
    error,
  };
};

const ProjectList: React.FC<ProjectListProps> = makeComp(
  props => {
    const projectListProps = useProjectListProps(props);
    return <ProjectListInner {...props} {...projectListProps} />;
  },
  {id: 'ProjectList'}
);

export default ProjectList;

const useAllProjectsProps = ({
  entityName,
  order,
  limit,
}: ProjectListPropsWithEntityName) => {
  const {loading, error, data} = useAllProjectsQuery({
    variables: {entityName, order, first: limit},
    fetchPolicy: 'cache-and-network',
  });
  let ps: any[] = [];
  let enableSortSettings = false;
  if (!loading && data != null) {
    const {entity, user, projects} = data;
    if (entity != null && (entity.memberCount > 1 || user == null)) {
      ps = projects!.edges;
      enableSortSettings = true;
    } else {
      ps = user!.projects!.edges;
    }
  }
  return {
    loading,
    error,
    projects: ps,
    enableSortSettings,
  };
};

export const ProjectListForEntity: React.FC<ProjectListPropsWithEntityName> = makeComp(
  props => {
    const allProjectsProps = useAllProjectsProps(props);
    return <ProjectListInner {...props} {...allProjectsProps} />;
  },
  {id: 'ProjectListForEntity'}
);
